import React from 'react';

const JobStatusStep = ({ data, updateData, onNext, onPrevious, isValid, isFirstStep, isLastStep, isSubmitting }) => {
  const jobStatusOptions = [
    {
      id: 'actively-looking',
      label: 'Yes, I\'m actively looking for a new job',
      description: 'Ready to explore new opportunities and make a move soon'
    },
    {
      id: 'open-to-opportunities',
      label: 'Not really, but would consider the right opportunity',
      description: 'Happy where I am but open to exceptional opportunities'
    },
    {
      id: 'not-interested',
      label: 'No, I\'m not interested right now',
      description: 'Focused on my current role and not looking to change'
    }
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isValid) {
      onNext();
    }
  };

  const handleOptionSelect = (optionId) => {
    updateData('jobSearchStatus', optionId);
  };

  return (
    <div className="space-y-8 md:space-y-12">
      <form onSubmit={handleSubmit} className="space-y-8 md:space-y-12">
        {/* Question */}
        <div className="text-center space-y-4">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white leading-tight">
            Are you looking for a job?
          </h1>
          <p className="text-lg md:text-xl text-gray-600 dark:text-gray-400">
            Help us understand your job search preferences
          </p>
        </div>

        {/* Enhanced Radio Group with Animations */}
        <div className="space-y-4">
          {jobStatusOptions.map((option, index) => (
            <label
              key={option.id}
              className={`relative flex cursor-pointer rounded-2xl px-6 py-6 border-2 transition-all duration-300 ease-in-out transform hover:scale-102 ${
                data.jobSearchStatus === option.id
                  ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 ring-4 ring-purple-500 ring-opacity-20 shadow-lg hover:shadow-xl'
                  : 'border-gray-200 dark:border-gray-600 hover:border-purple-300 dark:hover:border-purple-500 hover:bg-gray-50 dark:hover:bg-gray-700 hover:shadow-md'
              }`}
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <div className="flex w-full items-center">
                <div className="flex items-center mr-5">
                  {/* Enhanced Radio circle */}
                  <div
                    className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-300 ease-in-out transform ${
                      data.jobSearchStatus === option.id
                        ? 'border-purple-500 bg-purple-500 shadow-md scale-110'
                        : 'border-gray-300 dark:border-gray-500 hover:border-purple-400 hover:scale-105'
                    }`}
                  >
                    {data.jobSearchStatus === option.id && (
                      <div className="w-3 h-3 rounded-full bg-white animate-pulse"></div>
                    )}
                  </div>
                </div>
                <div className="flex-1">
                  <p className={`text-lg font-semibold mb-2 transition-colors duration-300 ${
                    data.jobSearchStatus === option.id
                      ? 'text-purple-900 dark:text-purple-100'
                      : 'text-gray-900 dark:text-white'
                  }`}>
                    {option.label}
                  </p>
                  <p className={`text-base transition-colors duration-300 ${
                    data.jobSearchStatus === option.id
                      ? 'text-purple-700 dark:text-purple-300'
                      : 'text-gray-500 dark:text-gray-400'
                  }`}>
                    {option.description}
                  </p>
                </div>
              </div>
              
              {/* Hidden radio input */}
              <input
                type="radio"
                name="jobSearchStatus"
                value={option.id}
                checked={data.jobSearchStatus === option.id}
                onChange={() => handleOptionSelect(option.id)}
                className="sr-only"
              />
            </label>
          ))}
        </div>

        {/* Enhanced Navigation Buttons */}
        <div className="flex justify-between items-center border-none !border-0">
          {!isFirstStep && (
            <button
              type="button"
              onClick={onPrevious}
              className="px-6 py-3 rounded-lg text-lg font-medium text-gray-600 dark:text-gray-400 
                       hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 
                       dark:hover:bg-gray-700 transition-all duration-300 ease-in-out
                       transform hover:scale-105 active:scale-95 focus:ring-4 focus:ring-gray-500/20"
            >
              Back
            </button>
          )}
          
          <button
            type="submit"
            disabled={!isValid || isSubmitting}
            className={`px-8 py-4 rounded-lg text-lg font-medium transition-all duration-300 ease-in-out transform ${
              isValid && !isSubmitting
                ? 'bg-purple-600 hover:bg-purple-700 text-white shadow-lg hover:shadow-xl hover:shadow-purple-500/25 hover:scale-105 active:scale-95 focus:ring-4 focus:ring-purple-500/20'
                : 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed opacity-60'
            } ${isFirstStep ? 'ml-auto' : ''}`}
          >
            {isSubmitting ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                {isLastStep ? 'Completing Setup...' : 'Continue'}
              </div>
            ) : (
              <span className="flex items-center">
                {isLastStep ? (
                  <>
                    Complete Setup
                    <span className="ml-2 transition-transform duration-300">🎉</span>
                  </>
                ) : (
                  <>
                    Continue
                    <span className={`ml-2 transition-transform duration-300 ${isValid ? 'translate-x-0' : '-translate-x-1'}`}>
                      →
                    </span>
                  </>
                )}
              </span>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default JobStatusStep; 